/**
 * This file enhances Video.js with custom components, features, and overrides.
 * 
 * This script customizes the Video.js player by adding a custom remaining time display, settings menu,
 * and settings button component. It includes functionality for displaying tooltips and toggling audio
 * description version of videos. Also included are functions to add custom accessibility overrides to
 * the default Video.js setup.
 * 
 * Any type of override should take place in this file. The main Video.js file should never be modified. 
 * 
 * @author Nick Tesh
 */
document.addEventListener("DOMContentLoaded", function () {
    const Component = videojs.getComponent("Component");
    const RemainingTimeDisplay = videojs.getComponent("RemainingTimeDisplay");
    const Button = videojs.getComponent("Button");

    const getSettingsMenu = (player) => player.controlBar?.getChild("SettingsMenu");

    const focusFirstSettingsMenuControl = (menuEl) => {
        const focusableElements = menuEl.querySelectorAll("input, select, button");

        for (let i = 0; i < focusableElements.length; i++) {
            if (focusableElements[i].offsetParent !== null) {
                focusableElements[i].focus();
                return;
            }
        }
    };

    const applyFocusTrap = (element) => {
        const focusableElements = element.querySelectorAll("input, select, button");
        const firstFocusableElement = focusableElements[0];
        const lastFocusableElement = focusableElements[focusableElements.length - 1];

        firstFocusableElement?.addEventListener("keydown", (event) => {
            if (event.key === "Tab" && event.shiftKey) {
                event.preventDefault();
                lastFocusableElement.focus();
            }
        });

        lastFocusableElement?.addEventListener("keydown", (event) => {
            if (event.key === "Tab" && !event.shiftKey) {
                event.preventDefault();
                firstFocusableElement.focus();
            }
        });
    };

    const srAnnounce = (() => {
        let timeout;
        return (message) => {
            if (timeout) {
                clearTimeout(timeout);
            }
            timeout = setTimeout(() => {
                const div = document.createElement("div");
                div.setAttribute("aria-live", "assertive");
                div.style.width = "0";
                div.style.height = "0";
                div.style.overflow = "hidden";
                document.body.insertAdjacentElement("afterbegin", div);
                div.innerHTML = message;
                setTimeout(() => {
                    div.remove();
                }, 10000);
            }, 1000);
        };
    })();

    // Custom RemainingTimeDisplay component
    class CustomRemainingTimeDisplay extends RemainingTimeDisplay {
        constructor(player, options) {
            super(player, options);

            // Add custom class for styling
            this.addClass("vjs-custom-remaining-time");

            // Update the content on player events
            player.on("timeupdate", () => this.updateContent());
            player.on("loadedmetadata", () => this.updateContent());
        }

        updateContent() {
            if (!this.player_.duration()) {
                return;
            }

            const currentTime = this.player_.currentTime();
            const duration = this.player_.duration();

            // Custom format: currentTime / duration
            const formatTime = (seconds) => {
                const minutes = Math.floor(seconds / 60);
                const secs = Math.floor(seconds % 60);
                return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
            };

            this.el().textContent = `${formatTime(currentTime)} / ${formatTime(duration)}`;
        }
    }

    videojs.registerComponent("CustomRemainingTimeDisplay", CustomRemainingTimeDisplay);

    // Custom Settings Menu component
    class SettingsMenu extends Component {
        constructor(player, options) {
            super(player, options);
            this.hide();
            this.on(player, "settingsmenu", this.toggle);
            this.on(player, "controlshidden", this.hide);
            this.keyboardActivated = false;

            const settingsButton = player.controlBar.getChild("SettingsButton").el();

            settingsButton.addEventListener("keydown", (event) => {
                if (event.key === " " || event.key === "Enter") {
                    this.keyboardActivated = true;
                    return;
                }

                if (event.key === "Tab" && !event.shiftKey && this.isOpen()) {
                    event.preventDefault();
                    focusFirstSettingsMenuControl(this.el());
                }
            });

            settingsButton.addEventListener("click", () => {
                this.keyboardActivated = false;
            });
        }
    
        createEl() {
            let uniqueId = this.player_.id() + "-" + Math.floor(Math.random() * 10000);
            let menuId = `settings-menu-${this.player_.id()}`;
            let audioDescriptionSource = this.player_.el().getAttribute("data-audio-description-source");
            let descriptionsButtonHTML = "";

            if (audioDescriptionSource) {
                descriptionsButtonHTML = `
                    <div class="vjs-settings-button vjs-toggle-audio-description">
                        <span>Audio Descriptions</span>
                        <label class="switch" for="audio-description-toggle-${uniqueId}">
                            <span class="visually-hidden">Toggle Audio Description</span>
                            <input type="checkbox" id="audio-description-toggle-${uniqueId}" class="audio-description-toggle-checkbox" tabindex="0" aria-checked="false">
                            <span class="slider"></span>
                        </label>
                    </div>`;
            }

            let el = super.createEl("div", {
                className: "vjs-settings-menu vjs-hidden",
                id: menuId,
                innerHTML: `
                    ${descriptionsButtonHTML}
                    <div class="vjs-settings-button vjs-playback-speed">
                        <span>Speed</span>
                        <select id="playback-${uniqueId}" class="vjs-playback-rate-selector" aria-label="Playback Speed" tabindex="0">
                            <option value="0.5">0.5x</option>
                            <option value="0.75">0.75x</option>
                            <option value="1" selected>Normal</option>
                            <option value="1.25">1.25x</option>
                            <option value="1.5">1.5x</option>
                            <option value="2">2x</option>
                        </select>
                    </div>`,
            });

            applyFocusTrap(el);

            // Add event listeners for toggles
            if (audioDescriptionSource) {
                this.addToggleEventListeners(el, ".audio-description-toggle-checkbox", "toggleaudiodescription");
            }

            // Add event listener for playback rate selector
            el.querySelector(".vjs-playback-rate-selector").addEventListener("change", (event) => {
                const selectedSpeed = parseFloat(event.target.value);
                this.player_.playbackRate(selectedSpeed);

                // Move the "selected" attribute to the selected option
                const options = event.target.options;
                for (let i = 0; i < options.length; i++) {
                    options[i].removeAttribute("selected");
                }
                options[event.target.selectedIndex].setAttribute("selected", "");
            });

            return el;
        }
    
        addToggleEventListeners(el, selector, eventName) {
            const toggle = el.querySelector(selector);
    
            toggle.addEventListener("change", () => {
                toggle.setAttribute("aria-checked", toggle.checked ? "true" : "false");
                this.player_.trigger(eventName);
            });
    
            toggle.addEventListener("keydown", (event) => {
                if (event.key === " " || event.key === "Enter") {
                    event.preventDefault();
                    toggle.click();
                }
            });
    
            toggle.addEventListener("focus", (event) => {
                event.target.closest(".switch").classList.add("focus-visible");
            });
    
            toggle.addEventListener("blur", (event) => {
                event.target.closest(".switch").classList.remove("focus-visible");
            });
        }
    
        isOpen() {
            return !this.hasClass("vjs-hidden");
        }

        toggle() {
            if (!this.isOpen()) {
                this.show();
                if (this.keyboardActivated) {
                    setTimeout(() => focusFirstSettingsMenuControl(this.el()), 150);
                }
            } else {
                this.hide();
            }
        }
    }
    
    videojs.registerComponent("SettingsMenu", SettingsMenu);

    // Custom Settings Button component
    class SettingsButton extends Button {
        constructor(player, options) {
            super(player, options);
            this.controlText("Settings");
            this.el().setAttribute("aria-haspopup", "dialog");
            const setExpandedAttribute = () => {
                this.el().setAttribute("aria-expanded", "false");
            };
            setExpandedAttribute();
            player.on("pause", setExpandedAttribute);
            player.on("play", setExpandedAttribute);
        }

        handleClick(event) {
            if (event) {
                event.stopPropagation();
            }

            this.player_.trigger("settingsmenu");
            const settingsMenu = getSettingsMenu(this.player_);
            this.el().setAttribute("aria-expanded", settingsMenu?.isOpen() ? "true" : "false");
        }

        handleKeyDown(event) {
            if (event.key === "Enter" || event.key === " ") {
                const settingsMenu = getSettingsMenu(this.player_);
                if (settingsMenu) {
                    settingsMenu.keyboardActivated = true;
                }
            }

            return super.handleKeyDown(event);
        }

        buildCSSClass() {
            return `vjs-settings-button ${super.buildCSSClass()}`;
        }
    }

    videojs.registerComponent("SettingsButton", SettingsButton);
    
    // Disable captions by default
    function disableCaptionsByDefault(player) {
        player.ready(function () {
            var tracks = player.textTracks();
            for (var i = 0; i < tracks.length; i++) {
                var track = tracks[i];
                if (track.kind === "captions") {
                    track.mode = "disabled";
                }
            }
        });
    }
    
    // Focus management for captions modal
    function manageCaptionsModalFocus(player) {
        const captionsButton = player.controlBar.getChild("CaptionsButton")?.el().querySelector("button");
        if (!captionsButton) return;
        const modalDialog = player.el().querySelector(".vjs-modal-dialog.vjs-text-track-settings");

        modalDialog.addEventListener("keydown", function (event) {
            if (event.key === "Escape") {
                setTimeout(() => {
                    captionsButton.focus();
                }, 1);
            }
        });

        if (modalDialog.querySelector(".vjs-done-button")) {
            modalDialog.querySelector(".vjs-done-button").addEventListener("click", function () {
                setTimeout(() => {
                    captionsButton.focus();
                }, 1);
            });
        }

        if (modalDialog.querySelector(".vjs-close-button")) {
            modalDialog.querySelector(".vjs-close-button").addEventListener("click", function () {
                setTimeout(() => {
                    captionsButton.focus();
                }, 0);
            });
        }
    }
    
    // This function updates the aria-labels of elements within the captions settings dialog
    function updateCaptionsDialogLabels() {
        const captionsDialogMain = document.querySelector(".vjs-modal-dialog.vjs-text-track-settings");
        if (captionsDialogMain) {
            // Update Close button
            const closeButton = captionsDialogMain.querySelector(".vjs-close-button");
            if (closeButton) {
                closeButton.removeAttribute("title");
                closeButton.setAttribute("aria-label", "Close Captions Settings Dialog");
            }

            // Update Reset button
            const resetButton = captionsDialogMain.querySelector(".vjs-default-button");
            if (resetButton) {
                resetButton.removeAttribute("title");
                resetButton.setAttribute("aria-label", "Reset - Restore all settings to the default values");
            }

            // Update Done button
            const doneButton = captionsDialogMain.querySelector(".vjs-done-button");
            if (doneButton) {
                doneButton.removeAttribute("title");
                doneButton.setAttribute("aria-label", "Done - Applies new selected settings");
            }
        }
    }
    
    // This function allows for both the Captions and Settings menu to be closed by pressing the escape key
    function addEscapeKeyListenerForMenus(player) {
        document.addEventListener("keydown", function (event) {
            if (event.key === "Escape") {
                // Close captions menu
                const captionsButton = player.controlBar.getChild("CaptionsButton")?.el().querySelector("button");
                const captionsMenu = player.el().querySelector(".vjs-captions-button .vjs-menu");
                if (captionsMenu && captionsMenu.classList.contains("vjs-lock-showing")) {
                    captionsMenu.classList.remove("vjs-lock-showing");
                    captionsMenu.classList.add("vjs-hidden");
                    captionsButton?.setAttribute("aria-expanded", "false");
                    // After closing focus back on button
                    setTimeout(() => {
                        captionsButton?.focus();
                    }, 1);
                }

                // Close settings menu
                const settingsButton = player.controlBar.getChild("SettingsButton").el();
                const settingsMenu = player.el().querySelector(".vjs-settings-menu");
                if (settingsMenu && !settingsMenu.classList.contains("vjs-hidden")) {
                    settingsMenu.classList.add("vjs-hidden");
                    settingsButton.setAttribute("aria-expanded", "false");
                    // After closing focus back on button
                    setTimeout(() => {
                        settingsButton.focus();
                    }, 1);
                }
            }
        });
    }

    // Select all video elements with the 'video-js' class
    let videos = document.querySelectorAll(".video-js");

    // Iterate over each video element and initialize Video.js if not already initialized
    videos.forEach(function (videoElement) {
        const isAudioDescribedVideo = videoElement.matches("[data-audio-description-source]");

        if (!videoElement.player) {
            const children = ["playToggle", "volumePanel", "CustomRemainingTimeDisplay", "progressControl", "liveDisplay", "captionsButton", "SettingsButton", "fullscreenToggle"];

            let player = videojs(videoElement, {
                enableDocumentPictureInPicture: false,
                controlBar: {
                    children,
                },
            });

            // Disable captions by default
            disableCaptionsByDefault(player);

            // Manage focus for captions modal
            manageCaptionsModalFocus(player);

            // Update aria-labels of elements inside captions dialog
            updateCaptionsDialogLabels();

            // Allow menus to be closed by pressing escape
            addEscapeKeyListenerForMenus(player);

            const videoAriaLabel =
                videoElement.getAttribute("data-video-aria-label") ||
                videoElement.getAttribute("aria-label") ||
                videoElement.getAttribute("data-video-title") ||
                "Video Player";
            player.el().setAttribute("aria-label", videoAriaLabel);

            const controlBar = player.controlBar;
            const settingsButton = controlBar.getChild("SettingsButton");
            const settingsButtonIndex = controlBar.children().indexOf(settingsButton);
            controlBar.addChild("SettingsMenu", {}, settingsButtonIndex + 1);

            const settingsMenu = getSettingsMenu(player);
            if (settingsMenu?.el()?.id) {
                settingsButton.el().setAttribute("aria-controls", settingsMenu.el().id);
            }

            player.on("toggleaudiodescription", function () {
                let currentTime = player.currentTime();
                let wasPlaying = !player.paused();
                let currentPlaybackRate = player.playbackRate();
                let audioDescriptionEnabled = player.el().querySelector(".audio-description-toggle-checkbox")?.checked;

                let normalSource = videoElement.getAttribute("data-standard-source");
                let audioDescriptionSource = videoElement.getAttribute("data-audio-description-source");

                const captionsButton = player.controlBar.getChild("CaptionsButton")?.el().querySelector("button");

                if (audioDescriptionSource && audioDescriptionEnabled) {
                    player.src({ type: "video/mp4", src: audioDescriptionSource });
                    showTemporaryMessage(player, "Using the audio described version of this video.");
                    setCaptionsButtonAttributes({ button: captionsButton, disabled: true, player });
                    disableCaptionsByDefault(player);
                } else {
                    player.src({ type: "video/mp4", src: normalSource });
                    showTemporaryMessage(player, "Using the non-described version of this video.");
                    setCaptionsButtonAttributes({ button: captionsButton, disabled: false, player });
                }

                player.one("loadeddata", function () {
                    player.currentTime(currentTime);
                    player.playbackRate(currentPlaybackRate);
                    if (wasPlaying) {
                        player.play();
                    }
                });
            });

            // Close settings menu if any control button is clicked
            player.on("click", function (e) {
                if (!e.target.closest(".vjs-settings-menu") && !e.target.closest(".vjs-settings-button")) {
                    player.trigger("controlshidden");
                }
            });

            // This function creates a temporary message element inside the video
            function showTemporaryMessage(player, message) {
                // Remove any existing message elements
                const existingMessageElement = player.el().querySelector(".vjs-message");
                if (existingMessageElement) {
                    existingMessageElement.remove();
                }

                const messageElement = document.createElement("div");
                messageElement.className = "vjs-message";
                messageElement.textContent = message;
                messageElement.setAttribute("role", "alert");
                messageElement.setAttribute("aria-live", "assertive");

                const wrapper = player.el().querySelector(".videojs-wrapper");
                if (wrapper) {
                    wrapper.appendChild(messageElement);
                } else {
                    player.el().appendChild(messageElement);
                }

                setTimeout(() => {
                    messageElement.classList.add("fade-out");
                    setTimeout(() => {
                        if (messageElement.parentNode) {
                            messageElement.parentNode.removeChild(messageElement);
                        }
                    }, 1000);
                }, 3000);
            }

            function setCaptionsButtonAttributes({ button, disabled, player }) {
                if (!button) return;
                
                const DISABLED_MSG_ID = `cc-disabled-msg-${player.id()}`;
                const clickDisabledCaptionsButton = (e) => {
                    if (button.getAttribute("aria-disabled") === "true") {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        showTemporaryMessage(player, "Captions are not available for this audio description");
                        return;
                    }
                };
                button.addEventListener("click", clickDisabledCaptionsButton);
                button.addEventListener("keydown", (evt) => {
                    if (evt.key === "Enter" || evt.key === " ") {
                        clickDisabledCaptionsButton(evt);
                    }
                });
                if (disabled) {
                    button.setAttribute("aria-disabled", "true");
                    let disabledMsg = document.createElement("span");

                    disabledMsg.classList.add("visually-hidden");
                    disabledMsg.id = DISABLED_MSG_ID;
                    disabledMsg.textContent = "Captions are not available for this audio description.";
                    button.setAttribute("aria-describedby", DISABLED_MSG_ID);
                    button.after(disabledMsg);
                } else {
                    button.removeAttribute("aria-disabled");
                    button.removeAttribute("aria-describedby");
                    document.getElementById(DISABLED_MSG_ID)?.remove();
                }
            }
        }
    });
});

/**
 * Custom title tooltips for Video.js control buttons.
 * 
 * This script adds tooltips to Video.js control buttons, enhancing their accessibility by displaying titles
 * when the buttons are focused or hovered over. It also includes adding custom classes to specific buttons
 * for additional override settings.
 * 
 *  @author Nick Tesh
 */
document.addEventListener("DOMContentLoaded", function () {
    const buttons = document.querySelectorAll(".video-js .vjs-control");

    buttons.forEach((button) => {
        // Check for specific buttons and assign custom classes
        const buttonTypes = {
            "vjs-play-control": "vjs-play-tooltip",
            "vjs-mute-control": "vjs-mute-tooltip",
            "vjs-captions-button": "vjs-captions-tooltip",
            "vjs-fullscreen-control": "vjs-fullscreen-tooltip",
        };

        // Determine the appropriate tooltip class
        let tooltipClass = null;
        for (const [className, tooltipClassName] of Object.entries(buttonTypes)) {
            if (button.classList.contains(className)) {
                tooltipClass = tooltipClassName;
                break;
            }
        }

        // Target the inner button for tooltips
        const targetButton = button.querySelector("button") || button;
        let initialTitle = targetButton.getAttribute("title");

        // Create the tooltip element
        if (initialTitle || tooltipClass) {
            const tooltip = document.createElement("span");
            tooltip.classList.add("vjs-tooltip");
            tooltip.classList.add("vjs-custom-tooltip");
            tooltip.setAttribute("aria-hidden", "true");
            const tooltipText = document.createElement("span");
            tooltipText.classList.add("vjs-tooltip-text", tooltipClass);

            // Set the initial tooltip text
            tooltipText.innerText = initialTitle;

            tooltip.appendChild(tooltipText);
            targetButton.appendChild(tooltip);

            // Show and hide the tooltip
            const showTooltip = () => {
                tooltipText.style.visibility = "visible";
                tooltip.style.opacity = 1;
                targetButton.removeAttribute("title");
            };

            const hideTooltip = () => {
                tooltipText.style.visibility = "hidden";
                tooltip.style.opacity = 0;

                if (tooltipText.style.visibility === "hidden") {
                    targetButton.setAttribute("title", tooltipText.innerText);
                }
            };

            targetButton.addEventListener("mouseenter", showTooltip);
            targetButton.addEventListener("mouseleave", hideTooltip);
            targetButton.addEventListener("focus", showTooltip);
            targetButton.addEventListener("blur", hideTooltip);

            // Function to update the tooltip text and title dynamically
            const updateTooltip = () => {
                if (button.classList.contains("vjs-play-control")) {
                    if (button.classList.contains("vjs-ended")) {
                        tooltipText.innerText = "Replay";
                    } else if (button.classList.contains("vjs-playing")) {
                        tooltipText.innerText = "Pause";
                    } else {
                        tooltipText.innerText = "Play";
                    }
                } else if (button.classList.contains("vjs-mute-control")) {
                    const isMuted = button.classList.contains("vjs-vol-0");
                    tooltipText.innerText = isMuted ? "Unmute" : "Mute";
                }
            };

            // Listen for events to update the tooltip content dynamically
            const player = videojs(button.closest(".video-js"));
            if (button.classList.contains("vjs-play-control")) {
                player.on("play", updateTooltip);
                player.on("pause", updateTooltip);
                player.on("ended", updateTooltip);
            } else if (button.classList.contains("vjs-mute-control")) {
                player.on("volumechange", updateTooltip);
            }

            // Ensure the tooltip updates correctly on state change
            player.ready(updateTooltip);
        }
    });

    const dismissCustomTooltip = (tooltip) => {
        tooltip.style.opacity = 0;
        const targetButton = tooltip.closest(".video-js .vjs-control");
        const tooltipText = tooltip.querySelector(".vjs-tooltip-text");

        if (tooltipText) {
            tooltipText.style.visibility = "hidden";
            if (tooltipText.style.visibility === "hidden") {
                targetButton?.setAttribute("title", tooltipText.innerText);
            }
        }
    };

    const CLS_FORCE_HIDE_TOOLTIP = "eq-force-hide-tooltip";
    const dismissTooltip = (tooltip) => {
        tooltip.classList.add(CLS_FORCE_HIDE_TOOLTIP);
    };

    // mousemove to remove force hidden tooltips (videojs's built native tooltip)
    document.querySelectorAll(".eq-video-wrapper").forEach((video) => {
        video.addEventListener("mousemove", function () {
            video.querySelectorAll(`.${CLS_FORCE_HIDE_TOOLTIP}`).forEach((el) => {
                el.classList.remove(CLS_FORCE_HIDE_TOOLTIP);
            });
        });
    });

    // Handle Escape key globally
    document.addEventListener("keydown", function (event) {
        if (event.key === "Escape") {
            // Dismiss all tooltips
            document.querySelectorAll('[class^="vjs-"][class$="-tooltip"]').forEach((tooltip) => {
                const isCustomTooltip = tooltip.classList.contains("vjs-custom-tooltip");

                if (isCustomTooltip) {
                    dismissCustomTooltip(tooltip);
                    return;
                }

                dismissTooltip(tooltip);
            });
        }
    });
});

/**
 * Custom play/pause button functionality for Video.js.
 * 
 * This script adds custom play/pause buttons that interact with Video.js instances. When clicked,
 * the buttons toggle between play and pause states, update their aria-label for accessibility,
 * and can show the native Video.js controls on play if designed to do so.
 * 
 * The script integrates seamlessly with Video.js by leveraging its API to control video playback
 * and manage button visibility and state. It includes an exception for buttons associated with 
 * a page banner, using a different aria-label.
 * 
 * @author Nick Tesh
 */
document.addEventListener("DOMContentLoaded", function () {
    // Video Play/Pause Buttons
    let videoBtns = document.querySelectorAll(".video-control-btn");
    videoBtns.forEach(function (el) {
        // Get video id from attribute 'data-target'
        let targetVideo = document.getElementById(el.dataset.target);
        if (targetVideo) {
            let player = videojs(targetVideo.id);

            el.addEventListener("click", function (e) {
                let isPageBanner = el.classList.contains("page-banner-btn");

                if (player.paused()) {
                    player.play();
                    el.setAttribute("aria-label", isPageBanner ? "Pause Background Video" : "Pause Video");
                } else {
                    player.pause();
                    el.setAttribute("aria-label", isPageBanner ? "Play Background Video" : "Play Video");
                }
                el.classList.toggle("paused");
            });
        }
    });
});

/**
 * Handles toggling the visibility of transcript content when a corresponding button is clicked.
 * Adds scroll shadows to indicate overflow content in the transcript container.
 * Supports multiple transcript instances on the same page by matching buttons and containers 
 * based on unique IDs. 
 * Also allows closing the transcript using the Escape key, which returns focus to the trigger button.
 * 
 * @author Nick Tesh
 */
document.addEventListener("DOMContentLoaded", function () {
    // Add shadow elements to eq-transcript-container and handle scrolling
    function addScrollShadows() {
        // Add shadow elements to each .eq-transcript-container
        jq(".eq-transcript-container").each(function () {
            let container = jq(this);
            let wrapper = container.find(".eq-transcript-wrapper");

            // Add top and bottom shadows if they don't exist
            if (container.find(".top-shadow").length === 0) {
                container.prepend('<span class="top-shadow"></span>');
            }
            if (container.find(".bottom-shadow").length === 0) {
                container.append('<span class="bottom-shadow"></span>');
            }

            // Update shadows based on the scroll position
            function updateShadows() {
                const scrollTop = wrapper.scrollTop();
                const scrollHeight = wrapper.prop("scrollHeight");
                const clientHeight = wrapper.innerHeight();

                // Show/hide top shadow
                if (scrollTop > 0) {
                    container.find(".top-shadow").show();
                } else {
                    container.find(".top-shadow").hide();
                }

                // Show/hide bottom shadow
                if (scrollTop + clientHeight < scrollHeight) {
                    container.find(".bottom-shadow").show();
                } else {
                    container.find(".bottom-shadow").hide();
                }
            }

            // Attach the scroll event to update shadows
            wrapper.on("scroll", updateShadows);

            // Initialize shadows visibility
            updateShadows();
        });
    }

    // Call addScrollShadows on DOMContentLoaded
    addScrollShadows();

    // Function to close the transcript
    function closeTranscript(container, triggerButton, videoTitle) {
        container.hide(); // Hide the container
        triggerButton.innerHTML = '<span class="eqicon-DOC" aria-hidden="true"></span>View Text Transcript';
        triggerButton.setAttribute("aria-label", "View Text Transcript for " + videoTitle);
        triggerButton.focus();
    }

    // Get all transcript trigger buttons
    const triggers = document.querySelectorAll(".transcript-trigger");

    triggers.forEach(function (trigger) {
        trigger.addEventListener("click", function () {
            // Get the ID from the trigger button to match the corresponding content
            const id = this.id.split("-").pop();
            const container = jq("#transcript-container-" + id);
            const wrapper = container.find(".eq-transcript-wrapper");
            const videoTitle = this.getAttribute("data-video-title");

            // Toggle visibility of the container
            if (container.css("display") === "none") {
                container.show(); // Show the container
                this.innerHTML = '<span class="eqicon-DOC" aria-hidden="true"></span>Hide Text Transcript';
                this.setAttribute("aria-label", "Hide Text Transcript for " + videoTitle);

                // Move focus to the wrapper
                wrapper.focus();
                wrapper.scrollTop(0);

                // Update shadows after making the container visible
                setTimeout(() => {
                    container.find(".bottom-shadow").show(); // Show the bottom shadow by default
                    updateShadows(wrapper);
                }, 0);
            } else {
                closeTranscript(container, this, videoTitle);
            }
        });
    });

    // Function to update shadows for a specific wrapper
    function updateShadows(wrapper) {
        const container = jq(wrapper).closest(".eq-transcript-container");
        const scrollTop = jq(wrapper).scrollTop();
        const scrollHeight = jq(wrapper).prop("scrollHeight");
        const clientHeight = jq(wrapper).innerHeight();

        // Show/hide top shadow
        if (scrollTop > 0) {
            container.find(".top-shadow").show();
        } else {
            container.find(".top-shadow").hide();
        }

        // Show/hide bottom shadow
        if (scrollTop + clientHeight < scrollHeight) {
            container.find(".bottom-shadow").show();
        } else {
            container.find(".bottom-shadow").hide();
        }
    }

    // Handle Escape key globally
    document.addEventListener("keydown", function (event) {
        if (event.key === "Escape") {
            // Find the currently open container
            const openContainer = jq(".eq-transcript-container:visible");
            if (openContainer.length) {
                const id = openContainer.attr("id").split("-").pop();
                const triggerButton = document.getElementById("transcript-trigger-" + id);
                const videoTitle = triggerButton.getAttribute("data-video-title");

                closeTranscript(openContainer, triggerButton, videoTitle);
            }
        }
    });

    // Call addScrollShadows on window resize
    jq(window).resize(function () {
        addScrollShadows();
    });
});
